// Copyright (C) 2020 One More Game - All Rights Reserved
// Unauthorized copying of any files within this project, via any medium, is strictly prohibited
// Proprietary and confidential

import React, { useState, useEffect } from "react"
import { Link, graphql } from "gatsby"
import Layout from "../components/Layout"
import FullWidthImageSlider from "../components/FullWidthImageSlider"
import TenetSlider, { Tenet } from "../components/TenetSlider"
import Typewriter from "typewriter-effect"
import ChatDialogue, { ChatMessage } from "../components/ChatDialogue"
import * as styles from "./styles.module.scss"

const Hero: React.FC = () => {
  const [isShowChat, setShowChat] = useState(false)

  const messages: ChatMessage[] = [
    {
      id: 1,
      body: "Can we say that? We haven't launched",
    },
    {
      id: 2,
      body: "The lawyers said it's fine. No one believes marketing copy anyway",
      isSender: true,
    },
    {
      id: 3,
      body: "Oh, okay. Let's roll with it",
    },
  ]

  const headline = (
    <Typewriter
      wrapperClassName={styles.h1Main}
      onInit={typewriter => {
        typewriter
          .typeString(`<h1 class='${styles.h1Main}'>Games Gamers Love</h1>`)
          .pauseFor(500)
          .typeString(`<span class='${styles.h1MainRedLine}'>*</span>`)
          .pauseFor(500)
          .callFunction(() => {
            setShowChat(true)
          })
          .pauseFor(10000)
          .callFunction(() => {
            setShowChat(false)
          })
          .pauseFor(500)
          .deleteChars(1)
          .pauseFor(50)
          .typeString(`<sup class='${styles.h1MainTm}'>™</sup>`)
          .pauseFor(3000)
          .start()
      }}
      options={{
        loop: true,
        cursor: "_",
        delay: 100,
        wrapperClassName: styles.h1Main,
        cursorClassName: styles.typewriterCursor,
      }}
    />
  )

  let chatDialogue

  if (isShowChat) {
    chatDialogue = (
      <ChatDialogue className={styles.heroChatContainer} messages={messages} />
    )
  } else {
    // Render a placeholder div if we're not ready to show the
    // chat dialogue yet.
    chatDialogue = <div className={styles.heroChatContainer} />
  }

  return (
    <div className={styles.heroWrapper}>
      <div className={styles.hero}>
        <div className={styles.vertText}>
          <div className={styles.vertTextDot} />
          <span className={styles.vertTextLine} />
          One More Game
        </div>
        <div className={styles.headline}>
          {headline}
          {chatDialogue}
        </div>
        <div className={styles.introMain}>
          <p>
            One More Game is a fully remote startup and we make online games.
            Our team members have created genre-defining games like <i>Warcraft</i>
            , <i>Diablo</i>, <i>StarCraft</i> and <i>Guild Wars</i> that collectively have been
            played for tens of billions of hours. Our mission is to help players
            have more fun!
          </p>
        </div>
      </div>
    </div>
  )
}

interface Props {
  data
}

const HomePage: React.FC<Props> = ({ data }: Props) => {
  // jamie todo: instead of building this array of images ourselves, we
  // should instead read from a YAML file next to this page which has
  // the file and alt text for each image we want to display
  return (
    <Layout title="Home">
      <section className={styles.heroSection}>
        <Hero />
      </section>
      <section className={styles.imageCarousel}>
        <FullWidthImageSlider
          images={[
            {
              file: data.carouselImg1.childImageSharp.fluid,
              alt: "Downtown Seattle's Denny Triangle",
            },
            {
              file: data.carouselImg2.childImageSharp.fluid,
              alt: "Seattle's Space Needle",
            },
            {
              file: data.carouselImg3.childImageSharp.fluid,
              alt: "Seattle's Great Wheel",
            },
          ]}
        />
      </section>
      <section>
        <div className={styles.companyValues}>
          <h2 className={styles.companyValuesTitle}>
            Happy developers make better games.
          </h2>
          <div className={styles.companyValuesIntro}>
            <p>
              We've built our company around making our development process
              enjoyable so we can make magical experiences for you, the players.{" "}
              <br></br>- <Link to="/about-us">Get to know us</Link>
            </p>
          </div>
          <TenetSlider>
            <Tenet
              title="Delightful Games"
              image={data.valuesImg1.childImageSharp.fixed}
              alt=""
            >
              We aim to exceed your expectations and create something
              delightful. The little things matter.
            </Tenet>
            <Tenet
              title="Fast Iterations"
              image={data.valuesImg2.childImageSharp.fixed}
              alt=""
            >
              It’s not enough that a game works, it has to be fun. Focusing on
              building faster allows us more freedom to find the fun. <br></br>-{" "}
              <Link to="/blog/alpha-driven-development">
                Learn about our approach
              </Link>
            </Tenet>
            <Tenet
              title="Social Gaming"
              image={data.valuesImg3.childImageSharp.fixed}
              alt=""
            >
              We build online games because we love playing with friends, and we
              know you do too.
            </Tenet>
          </TenetSlider>
        </div>
      </section>
    </Layout>
  )
}

export default HomePage

// jamie todo: instead of querying for each of these images and running them
// through the transformer, why don't we pull this inforamtion from some YAML
// data files which can correspond with this page.
export const pageQuery = graphql`
  query {
    carouselImg1: file(relativePath: { eq: "mars/_V1A0185.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2400) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    carouselImg2: file(relativePath: { eq: "mars/_V1A0051.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2400) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    carouselImg3: file(relativePath: { eq: "mars/_V1A0375.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 2400) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    valuesImg1: file(relativePath: { eq: "developer-icon.png" }) {
      childImageSharp {
        fixed(width: 90, height: 90) {
          ...GatsbyImageSharpFixed_noBase64
        }
      }
    }
    valuesImg2: file(relativePath: { eq: "fast-icon.png" }) {
      childImageSharp {
        fixed(width: 90, height: 90) {
          ...GatsbyImageSharpFixed_noBase64
        }
      }
    }
    valuesImg3: file(relativePath: { eq: "create-icon.png" }) {
      childImageSharp {
        fixed(width: 90, height: 90) {
          ...GatsbyImageSharpFixed_noBase64
        }
      }
    }
  }
`
